import {AbstractControl, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {
  IDocumentFormGroup,
  IDocumentInfoFormGroup
} from '../../services/forms/new-case-filing-form/new-case-filing-form.service';
import {DeterminationCategoryIdEnum} from '../../../constants';
import {ICaseDocketEntryFormGroup} from '../../services/forms/case-form/case-form.service';
import {
  IDeterminationFormGroup
} from '../../services/forms/existing-case-filing-form/existing-case-filing-form.service';

/**
 * Returns a document's DL/IR determination form, or undefined when it has none or only the empty placeholder group
 * that the form builders seed: CaseFormService on every docket entry document (buildCaseDocketEntryFromDTO,
 * addSupportingDocument) and NewCaseFilingFormService on e-filing supporting documents (addSupportingDocument).
 * The placeholder is present but has no controls, so use this (or hasDeterminationForm) rather than
 * `controls.determination`.
 * @param documentFG the main or supporting document form group
 * @returns the determination form group, or undefined when there is no real one
 */
export function getDeterminationForm(documentFG: FormGroup<IDocumentFormGroup>): FormGroup<IDeterminationFormGroup> | undefined {
  const determinationFG = documentFG.controls.determination;
  return determinationFG?.controls.category ? determinationFG : undefined;
}

/**
 * Whether a document has a real DL/IR determination form: not missing, and not the empty placeholder group
 * (see getDeterminationForm).
 * @param documentFG the main or supporting document form group
 * @returns true when the document's determination form has its controls
 */
export function hasDeterminationForm(documentFG: FormGroup<IDocumentFormGroup>): boolean {
  return !!getDeterminationForm(documentFG);
}

/**
 * Validator for ecf determination award amount.
 */
export function ecfDeterminationAwardAmountValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const documentFG = control as FormGroup<IDocumentInfoFormGroup>;
    const determinationFG = documentFG?.controls.mainDocument?.controls.determination;
    if (!determinationFG || determinationFG.controls.category.value !== DeterminationCategoryIdEnum.Award) {
      return null;
    }
    return compareSums(determinationFG) ? null : { awardAmountNotEqual: true };
  };
}

/**
 * Validator for docket entry determination award amount.
 */
export function docketEntryDeterminationAwardAmountValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const determinationFG = getDeterminationForm((control as FormGroup<ICaseDocketEntryFormGroup>).controls.mainDocument);

    // Only an Award has award amounts to compare; there are none without a determination form (or with the placeholder).
    if (determinationFG?.controls.category.value !== DeterminationCategoryIdEnum.Award) {
      return null;
    }
    return compareSums(determinationFG) ? null : { awardAmountNotEqual: true };
  };
}

/**
 * Helper function to compare the total award amount to the sum of award types amounts.
 * @param determinationFG
 */
function compareSums(determinationFG: FormGroup<IDeterminationFormGroup>): boolean {
  const awardTypes = determinationFG.controls.awardTypes.controls;
  const totalAwardAmount = determinationFG.controls.totalAwardAmount.value;
  const sum = awardTypes.reduce((acc, ctrl) => acc + (ctrl.value.amount ?? 0), 0);
  // Amounts are currency values (2 decimal places). Floating point addition can
  // introduce tiny rounding errors (e.g. 18835.44 vs 18835.440000000002), so we
  // compare the values rounded to the nearest cent rather than with strict equality.
  return roundToCents(totalAwardAmount ?? 0) === roundToCents(sum);
}

/**
 * Rounds a currency value to the nearest cent, avoiding floating point comparison errors.
 * @param value
 */
function roundToCents(value: number): number {
  return Math.round(value * 100) / 100;
}
